import React, { useEffect, useState } from "react";
import AdminLayout from "../../layout/AdminLayout";
import { useParams } from "react-router-dom";
import { getRestaurantById } from "../../../api/restaurants.api";
import { baseURL } from "../../../api/api"; // Assuming baseURL is defined
import { Link } from "react-router-dom";
import { FiPlus } from "react-icons/fi";
import { RotatingLines } from "react-loader-spinner";

function RestaurantDetails() {
  const { id } = useParams(); // Get the restaurant ID from the URL params
  const [restaurantDetails, setRestaurantDetails] = useState(null);
  const [currentImage, setCurrentImage] = useState(""); // To store the currently selected image
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getRestaurantById(id)
      .then((res) => {
        setRestaurantDetails(res?.data);
        if (res?.data?.images?.length > 0) {
          setCurrentImage(res?.data?.images[0]);
        }
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  // Function to render opening hours in a readable format
  const renderOpeningHours = (hours) => {
    return Object.keys(hours).map((day) => (
      <div
        key={day}
        className="shadow-md p-3 mx-2 sm:col-span-12 lg:col-span-4"
      >
        <label className="block text-base font-semibold leading-6 text-gray-600">
          {day.charAt(0).toUpperCase() + day.slice(1)}
        </label>
        <p className="mt-2 text-sm font-medium text-gray-600">
          {hours[day].start} - {hours[day].end}
        </p>
      </div>
    ));
  };

  // Handle image click and update the current image
  const handleImageClick = (image) => {
    setCurrentImage(image); // Update the current image when a thumbnail is clicked
  };

  // Function to construct the full image URL
  const getImageUrl = (imageName) => {
    console.log("Image Name", imageName); // Log the image name for debugging
    return `${baseURL}/s3/images/${imageName}`; // Prepending the base URL to form the full URL
  };

  return (
    <AdminLayout title="Fixed Location Details">
      {loading ? (
        <div className="flex justify-center items-center h-screen">
          <RotatingLines
            strokeColor="black"
            strokeWidth="5"
            animationDuration="0.75"
            width="96"
            visible={true}
          />
        </div>
      ) : (
        <>
          <div className="mb-5 flex items-center justify-end sticky top-16 bg-slate-50 py-3 z-10 px-2">
            <Link
              to={`/fixedLocations/edit/${id}`}
              className="flex items-center justify-center rounded-md bg-yellowColor px-3 py-2.5 text-base font-semibold text-gray-700 shadow-sm hover:bg-opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellowColor"
            >
              <FiPlus className="text-lg text-gray-700" />
              Edit Fixed Location
            </Link>
          </div>

          <div className="px-10 gap-5 grid grid-cols-12">
            {/* Left Panel with Restaurant Info */}
            <div className="lg:col-span-8 md:col-span-8 col-span-12">
              {/* Restaurant info */}
              <div className="py-0.5 md:py-3 lg:py-3 items-center grid grid-cols-12">
                {/* Name */}
                <div className="shadow-md p-3 mx-2 lg:col-span-3 md:col-span-3 col-span-12">
                  <label className="block text-base font-semibold leading-6 text-gray-600">
                    Name
                  </label>
                  <p className="mt-2 text-sm font-medium text-gray-600">
                    {restaurantDetails?.name}
                  </p>
                </div>
                {/* Location */}
                <div className="shadow-md p-3 mx-2 lg:col-span-3 md:col-span-3 sm:col-span-12">
                  <label className="block text-base font-semibold leading-6 text-gray-600">
                    Location
                  </label>
                  <p className="mt-2 text-sm font-medium text-gray-600">
                    {restaurantDetails?.location}
                  </p>
                </div>
                {/* Cuisine Type */}
                <div className="shadow-md p-3 mx-2 lg:col-span-3 md:col-span-3 sm:col-span-12">
                  <label className="block text-base font-semibold leading-6 text-gray-600">
                    Cuisine Type
                  </label>
                  <p className="mt-2 text-sm font-medium text-gray-600">
                    {restaurantDetails?.cuisineType}
                  </p>
                </div>
                {/* Seating Capacity */}
                <div className="shadow-md p-3 mx-2 lg:col-span-3 md:col-span-3 sm:col-span-12">
                  <label className="block text-base font-semibold leading-6 text-gray-600">
                    Seating Capacity
                  </label>
                  <p className="mt-2 text-sm font-medium text-gray-600">
                    {restaurantDetails?.seatingCapacity}
                  </p>
                </div>
                {/* Foot Traffic */}
                <div className="shadow-md p-3 mx-2 lg:col-span-3 mt-2 md:col-span-3 sm:col-span-12">
                  <label className="block text-base font-semibold leading-6 text-gray-600">
                    Foot Traffic Estimate
                  </label>
                  <p className="mt-2 text-sm font-medium text-gray-600">
                    {restaurantDetails?.footTrafficEstimate}
                  </p>
                </div>
                {/* Restaurant Status */}
                <div className="shadow-md p-3 mx-2 lg:col-span-3 md:col-span-3 sm:col-span-12">
                  <label className="block text-base font-semibold leading-6 text-gray-600">
                    Restaurant Status
                  </label>
                  <p
                    className={`mt-2 text-sm font-medium ${
                      restaurantDetails?.status === "Pending Approval"
                        ? "text-red-500"
                        : "text-green-500"
                    }`}
                  >
                    {restaurantDetails?.status}
                  </p>
                </div>
              </div>

              {/* Opening Hours */}
              <h2 className="text-xl font-semibold text-gray-700 ms-3 mt-2">
                Opening Hours
              </h2>
              <div className="py-0.5 md:py-3 lg:py-3 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12 mt-2">
                {renderOpeningHours(restaurantDetails?.openingHours || {})}
              </div>

              {/* Description */}
              {restaurantDetails?.description && (
                <div className="py-0.5 md:py-3 lg:py-3 items-center grid grid-cols-12 mt-5">
                  <div className="shadow-md p-3 mx-2 lg:col-span-12 md:col-span-12 col-span-12">
                    <label className="block text-base font-semibold leading-6 text-gray-600">
                      Description
                    </label>
                    <p className="mt-2 text-sm font-medium text-gray-600">
                      {restaurantDetails?.description}
                    </p>
                  </div>
                </div>
              )}

              {/* Images Section */}
              {restaurantDetails?.images?.length > 0 && (
                <div className="mt-5">
                  {/* Large Image */}
                  <div className="h-96 flex justify-center mb-4">
                    <img
                      src={currentImage ? getImageUrl(currentImage) : ""}
                      alt="Current"
                      className="w-full max-w-4xl h-auto shadow-lg rounded-lg"
                    />
                  </div>

                  {/* Thumbnails */}
                  <div className="flex justify-center space-x-4">
                    {restaurantDetails?.images?.map((image, index) => (
                      <img
                        key={index}
                        src={getImageUrl(image)}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-20 h-20 object-cover cursor-pointer rounded-md shadow-md hover:opacity-75"
                        onClick={() => handleImageClick(image)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Panel with Contact Information */}
            <div className="lg:col-span-4 md:col-span-4 col-span-12 mt-5">
              <div className="h-72 mt-2">
                <h2 className="text-xl font-semibold text-gray-700 mb-4 ms-2">
                  Contact Information
                </h2>
                <div className="shadow-md p-3 mx-2 mb-3">
                  <label className="block text-base font-semibold leading-6 text-gray-600">
                    Phone
                  </label>
                  <p className="mt-2 text-sm font-medium text-gray-600">
                    {restaurantDetails?.contactInformation?.phone}
                  </p>
                </div>
                {restaurantDetails?.contactInformation?.email && (
                  <div className="shadow-md p-3 mx-2 mb-3">
                    <label className="block text-base font-semibold leading-6 text-gray-600">
                      Email
                    </label>
                    <p className="mt-2 text-sm font-medium text-gray-600">
                      {restaurantDetails?.contactInformation?.email}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

export default RestaurantDetails;
